import React from 'react';
import { Heart, Droplets, Stethoscope, BookOpen, TrendingUp } from 'lucide-react';
import { SEO } from '../components/layout/SEO';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { FadeIn } from '../components/ui/FadeIn';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
export function CharityPage() {
  const {
    t,
    isRTL
  } = useLanguage();
  const currentProjects = [{
    icon: Droplets,
    title: 'Clean Water Initiative',
    desc: 'Installing water filtration plants and hand pumps in rural villages across KPK to prevent waterborne diseases and improve community health.',
    image: 'https://images.unsplash.com/photo-1541844053589-346841d0b34c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    status: 'Active',
    impact: '5 villages, 2,000+ people'
  }, {
    icon: Stethoscope,
    title: 'Free Medical Camps',
    desc: 'Providing free consultations, medicines, and minor surgeries to underserved populations in remote areas of Pakistan.',
    image: 'https://images.unsplash.com/photo-1666214280557-f1b5022eb634?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    status: 'Ongoing',
    impact: 'Quarterly camps, 500+ patients per camp'
  }, {
    icon: BookOpen,
    title: 'Medical Education Fund',
    desc: 'Scholarships for deserving medical students from KPK who cannot afford tuition fees, supporting the next generation of doctors.',
    image: 'https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    status: 'Active',
    impact: '15 students supported annually'
  }];
  const pastActivities = [{
    title: 'Flood Relief 2022',
    image: 'https://images.unsplash.com/photo-1547638375-ebf04735d792?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    desc: 'Emergency response delivering food, water, and medical supplies to flood-affected families.'
  }, {
    title: 'COVID-19 Response',
    image: 'https://images.unsplash.com/photo-1584036561566-baf8f5f1b144?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    desc: 'Providing PPE and medical equipment to hospitals in KPK during the pandemic peak.'
  }, {
    title: 'Eye Camp 2019',
    image: 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    desc: 'Over 500 patients screened and 150 cataract surgeries performed free of charge.'
  }];
  return <>
      <SEO title={t('nav.charity')} description={t('charity.hero.subtitle')} data-id="element-212" />

      {/* Hero */}
      <div className="relative bg-[#1e5f4f] text-white py-16 md:py-20 overflow-hidden" data-id="element-213">
        <div className="absolute inset-0 opacity-10" data-id="element-214">
          <img src="https://images.unsplash.com/photo-1593113598332-cd288d649433?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" alt="Humanitarian aid" className="h-full w-full object-cover" data-id="element-215" />
        </div>
        <div className="relative container mx-auto px-4 text-center" data-id="element-216">
          <FadeIn data-id="element-217">
            <Heart className="h-12 w-12 mx-auto mb-6 text-white" data-id="element-218" />
            <h1 className="text-4xl md:text-5xl font-serif font-bold mb-4 text-white" data-id="element-219">
              Serving Humanity
            </h1>
            <p className="text-xl text-white max-w-2xl mx-auto leading-relaxed" data-id="element-220">
              We believe healthcare is a fundamental human right. Our charity
              work brings relief, hope, and healing to those who need it most.
            </p>
          </FadeIn>
        </div>
      </div>

      {/* Past Charitable Activities */}
      <Section className="bg-gray-50" data-id="element-221">
        <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-222">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-primary-900 mb-4" data-id="element-223">
            {t('charity.past.title')}
          </h2>
          <p className="text-gray-600 text-lg" data-id="element-224">{t('charity.past.subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-225">
          {pastActivities.map((activity, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-226">
              <Card className="h-full overflow-hidden border-none shadow-md hover:shadow-lg transition-shadow" data-id="element-227">
                <div className="h-48 overflow-hidden bg-gray-200" data-id="element-228">
                  <img src={activity.image} alt={activity.title} className="w-full h-full object-cover" data-id="element-229" />
                </div>
                <CardContent className="p-6" data-id="element-230">
                  <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-231">
                    {activity.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed" data-id="element-232">
                    {activity.desc}
                  </p>
                </CardContent>
              </Card>
            </FadeIn>)}
        </div>
      </Section>

      {/* Current Activities */}
      <Section data-id="element-233">
        <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-234">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-primary-900 mb-4" data-id="element-235">
            {t('charity.current.title')}
          </h2>
          <p className="text-gray-600 text-lg" data-id="element-236">
            {t('charity.current.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-237">
          {currentProjects.map((project, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-238">
              <Card className="h-full overflow-hidden border-none shadow-lg hover:shadow-xl transition-shadow" data-id="element-239">
                <div className="h-48 overflow-hidden relative" data-id="element-240">
                  <img src={project.image} alt={project.title} className="w-full h-full object-cover" data-id="element-241" />
                  <div className="absolute top-4 right-4 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full" data-id="element-242">
                    {project.status}
                  </div>
                </div>
                <CardContent className="p-6" data-id="element-243">
                  <div className="h-12 w-12 mx-auto -mt-12 bg-white rounded-full flex items-center justify-center shadow-md mb-4 relative z-10" data-id="element-244">
                    <project.icon className="h-6 w-6 text-primary-600" data-id="element-245" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 text-center" data-id="element-246">
                    {project.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 leading-relaxed" data-id="element-247">
                    {project.desc}
                  </p>
                  <div className="pt-4 border-t border-gray-100" data-id="element-248">
                    <div className="flex items-center justify-center text-sm text-gray-700" data-id="element-249">
                      <TrendingUp className={`h-4 w-4 text-green-600 ${isRTL ? 'ml-2' : 'mr-2'}`} data-id="element-250" />
                      <span className="font-medium" data-id="element-251">{project.impact}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </FadeIn>)}
        </div>
      </Section>

      {/* Future Plans */}
      <Section background="gray" data-id="element-252">
        <div className="max-w-4xl mx-auto text-center" data-id="element-253">
          <div className="mb-8" data-id="element-254">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-primary-900 mb-4" data-id="element-255">
              {t('charity.future.title')}
            </h2>
            <p className="text-gray-600 text-lg" data-id="element-256">
              {t('charity.future.subtitle')}
            </p>
          </div>
        </div>
      </Section>

      {/* Donation CTA */}
      <Section background="primary" className="text-center" data-id="element-257">
        <FadeIn data-id="element-258">
          <Heart className="h-12 w-12 mx-auto mb-6 text-red-400" data-id="element-259" />
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6 text-white" data-id="element-260">
            {t('charity.donate.title')}
          </h2>
          <p className="text-white mb-8 max-w-2xl mx-auto text-lg leading-relaxed" data-id="element-261">
            {t('charity.donate.subtitle')}
          </p>

          <div className="mb-10" data-id="element-262">
            <a href="https://donate.stripe.com/test_charity_placeholder" target="_blank" rel="noopener noreferrer" data-id="element-263">
              <Button size="lg" variant="secondary" className="px-10 py-6 text-xl shadow-xl hover:scale-105 transition-transform" data-id="element-264">
                <Heart className={`h-6 w-6 ${isRTL ? 'ml-3' : 'mr-3'}`} data-id="element-265" />
                {t('charity.donate.button')}
              </Button>
            </a>
            <p className="text-white/80 text-sm mt-4" data-id="element-266">
              {t('charity.donate.stripeNote')}
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur rounded-xl p-8 max-w-md mx-auto mb-8" data-id="element-267">
            <h3 className="font-bold text-white mb-4 text-lg" data-id="element-268">
              {t('charity.donate.bankTitle')}
            </h3>
            <div className="space-y-2 text-left text-sm text-white/90" data-id="element-269">
              <div className="flex justify-between border-b border-white/20 pb-2" data-id="element-270">
                <span data-id="element-271">{t('charity.donate.bankName')}</span>
                <span className="font-medium text-white" data-id="element-272">Barclays Bank</span>
              </div>
              <div className="flex justify-between border-b border-white/20 pb-2" data-id="element-273">
                <span data-id="element-274">{t('charity.donate.accountName')}</span>
                <span className="font-medium text-white" data-id="element-275">KMA UK Charity</span>
              </div>
              <div className="flex justify-between border-b border-white/20 pb-2" data-id="element-276">
                <span data-id="element-277">{t('charity.donate.sortCode')}</span>
                <span className="font-medium text-white" data-id="element-278">20-00-00</span>
              </div>
              <div className="flex justify-between pb-2" data-id="element-279">
                <span data-id="element-280">{t('charity.donate.accountNo')}</span>
                <span className="font-medium text-white" data-id="element-281">12345678</span>
              </div>
            </div>
          </div>

          <Link to="/contact" data-id="element-282">
            <Button size="lg" variant="outline" className="text-white border-white hover:bg-white/10 hover:text-white" data-id="element-283">
              {t('charity.donate.contactButton')}
            </Button>
          </Link>
        </FadeIn>
      </Section>
    </>;
}